import type { RoiLine, RoiLineKind, RoiPeriod } from '../../lib/types';
import { isComputedLabor, lineAmount } from '../../lib/roi';

/** Editable calculator line: numeric inputs are kept as text so blanks survive while typing. */
export interface RoiLineDraft {
  key: number;
  kind: RoiLineKind;
  label: string;
  amount: string;
  period: RoiPeriod;
  hoursPerWeek: string;
  hourlyRate: string;
}

let nextKey = 1;

const numOrNull = (s: string): number | null => (s.trim() === '' ? null : Number(s));
const numText = (n: number | null | undefined): string => (n === null || n === undefined ? '' : String(n));

export function newDraftLine(): RoiLineDraft {
  return { key: nextKey++, kind: 'Labor', label: '', amount: '', period: 'PerYear', hoursPerWeek: '', hourlyRate: '' };
}

export function draftsFromLines(lines: RoiLine[] | null | undefined): RoiLineDraft[] {
  return (lines ?? []).map((l) => ({
    key: nextKey++,
    kind: l.kind,
    label: l.label ?? '',
    amount: numText(l.amount),
    period: l.period,
    hoursPerWeek: numText(l.hoursPerWeek),
    hourlyRate: numText(l.hourlyRate),
  }));
}

/** Draft to wire shape. A Labor line with both helper inputs gets the computed amount, per year. */
export function lineFromDraft(d: RoiLineDraft): RoiLine {
  const isLabor = d.kind === 'Labor';
  const line: RoiLine = {
    kind: d.kind,
    label: d.label.trim() === '' ? null : d.label.trim(),
    amount: numOrNull(d.amount) ?? 0,
    period: d.period,
    hoursPerWeek: isLabor ? numOrNull(d.hoursPerWeek) : null,
    hourlyRate: isLabor ? numOrNull(d.hourlyRate) : null,
  };
  if (isComputedLabor(line)) return { ...line, amount: lineAmount(line), period: 'PerYear' };
  return line;
}
